import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, PlusIcon, UserIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import EventService from '../../services/EventService';
import toast from 'react-hot-toast';

const CreateEventModal = ({ isOpen, onClose, selectedDate, onEventCreated }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    initialDate: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '',
    initialTime: '09:00',
    finalDate: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '',
    finalTime: '10:00'
  });
  const [participants, setParticipants] = useState([]);
  const [newParticipant, setNewParticipant] = useState('');
  const [loading, setLoading] = useState(false);

  // Resetear formulario cuando se abre el modal
  React.useEffect(() => {
    if (isOpen && selectedDate) {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      setFormData({
        name: '',
        description: '',
        initialDate: dateStr,
        initialTime: '09:00',
        finalDate: dateStr,
        finalTime: '10:00'
      });
      setParticipants([]);
      setNewParticipant('');
    }
  }, [isOpen, selectedDate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddParticipant = () => {
    if (newParticipant.trim() && !participants.includes(newParticipant.trim())) {
      setParticipants(prev => [...prev, newParticipant.trim()]);
      setNewParticipant('');
    }
  };

  const handleRemoveParticipant = (index) => {
    setParticipants(prev => prev.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAddParticipant();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('El nombre del evento es obligatorio');
      return;
    }

    if (!formData.initialDate || !formData.finalDate) {
      toast.error('Las fechas son obligatorias');
      return;
    }

    if (new Date(formData.initialDate + 'T' + formData.initialTime) >= 
        new Date(formData.finalDate + 'T' + formData.finalTime)) {
      toast.error('La fecha de fin debe ser posterior a la fecha de inicio');
      return;
    }

    setLoading(true);
    
    try {
      const eventData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        initialDate: formData.initialDate + 'T' + formData.initialTime + ':00',
        finalDate: formData.finalDate + 'T' + formData.finalTime + ':00',
        participants: participants.join(',')
      };

      await EventService.createEvent(eventData);
      
      toast.success('Evento creado exitosamente');
      onEventCreated();
      onClose();
    } catch (error) {
      toast.error('Error al crear el evento');
      console.error('Error creating event:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="card max-w-2xl w-full p-6"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Crear Nuevo Evento
              </h2>
              {selectedDate && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {format(selectedDate, "dd 'de' MMMM, yyyy", { locale: es })}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Nombre del evento */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nombre del Evento *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="input-field"
                placeholder="Ej: Sprint Planning, Reunión de equipo..."
                required
              />
            </div>

            {/* Descripción */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Descripción
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="input-field"
                placeholder="Describe el evento..."
              />
            </div>

            {/* Fechas y horas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Fecha de inicio */}
              <div>
                <label htmlFor="initialDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Fecha de Inicio *
                </label>
                <input
                  type="date"
                  id="initialDate"
                  name="initialDate"
                  value={formData.initialDate}
                  onChange={handleInputChange}
                  className="input-field"
                  required
                />
              </div>

              {/* Hora de inicio */}
              <div>
                <label htmlFor="initialTime" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Hora de Inicio
                </label>
                <input
                  type="time"
                  id="initialTime"
                  name="initialTime"
                  value={formData.initialTime}
                  onChange={handleInputChange}
                  className="input-field"
                />
              </div>

              {/* Fecha de fin */}
              <div>
                <label htmlFor="finalDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Fecha de Fin *
                </label>
                <input
                  type="date"
                  id="finalDate"
                  name="finalDate"
                  value={formData.finalDate}
                  onChange={handleInputChange}
                  className="input-field"
                  required
                />
              </div>

              {/* Hora de fin */}
              <div>
                <label htmlFor="finalTime" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Hora de Fin
                </label>
                <input
                  type="time"
                  id="finalTime"
                  name="finalTime"
                  value={formData.finalTime}
                  onChange={handleInputChange}
                  className="input-field"
                />
              </div>
            </div>

            {/* Participantes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Participantes
              </label>
              
              {/* Lista de participantes */}
              {participants.length > 0 && (
                <div className="mb-3 space-y-2">
                  {participants.map((participant, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <UserIcon className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{participant}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveParticipant(index)}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Eliminar
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Agregar participante */}
              <div className="flex space-x-2">
                <input
                  type="email"
                  value={newParticipant}
                  onChange={(e) => setNewParticipant(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="correo@ejemplo.com"
                  className="input-field flex-1"
                />
                <button
                  type="button"
                  onClick={handleAddParticipant}
                  className="btn-primary flex items-center space-x-2 px-4"
                  disabled={!newParticipant.trim()}
                >
                  <PlusIcon className="h-4 w-4" />
                  <span>Agregar</span>
                </button>
              </div>
            </div>

            {/* Botones */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Creando...</span>
                  </div>
                ) : (
                  'Crear Evento'
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CreateEventModal;























